import {
  Config,
  PixelStreaming,
} from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.4'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useRolodexEdit } from 'features/fde'
import type { KestrelViewerMessageCallbackAPI } from 'features/kestrelViewer'
import {
  getAllMessages,
  KestrelViewerProvider,
  KestrelViewerUI,
  startWebRTCStatsCollection,
  updateMarkup,
  useCameraPoses,
  useKestrel,
  usePhotoFiles,
} from 'features/kestrelViewer'
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Vector2 } from 'three'
import {
  G1N5,
  KVAnnotation,
  KVCameraPose,
  KVMeasurement,
  KVNavigationMode,
  KVPhoto,
  KVPolygon,
  KVPosition,
  KVRotation,
  KVSettings,
  KVSurfaceArea,
} from 'types'

/********* Helpers for message handling *********/
interface ChunkBuffer {
  chunks: string[]
  totalChunks: number
  receivedChunks: number
}

interface ParsedMessage {
  type?: string
  __chunkType?: 'partial'
  __messageId?: string
  __totalChunks?: number
  __chunkIndex?: number
  __chunkData?: string
  [key: string]: unknown
}

interface MessageHandler {
  type: string
  requiredData: string[]
  callback: (
    json: Record<string, unknown>,
    api: KestrelViewerMessageCallbackAPI
  ) => void
}

const processChunkedMessage = (
  messageId: string,
  messageChunks: Map<string, ChunkBuffer>,
  dataJson: {
    __totalChunks: number
    __chunkIndex: number
    __chunkData: string
  }
): string | null => {
  // Initialize chunk buffer if this is the first chunk
  if (!messageChunks.has(messageId)) {
    messageChunks.set(messageId, {
      chunks: new Array(dataJson.__totalChunks),
      totalChunks: dataJson.__totalChunks,
      receivedChunks: 0,
    })
  }

  const buffer = messageChunks.get(messageId)!

  // Store this chunk
  buffer.chunks[dataJson.__chunkIndex] = dataJson.__chunkData
  buffer.receivedChunks++

  // Check if we have all chunks
  if (buffer.receivedChunks === buffer.totalChunks) {
    // Reassemble the complete message
    const completeMessage = buffer.chunks.join('')
    messageChunks.delete(messageId)
    return completeMessage
  }

  return null
}

const handleMessageWithHandlers = (
  messageJson: ParsedMessage,
  messageHandlers: MessageHandler[],
  kestrelViewerAPI: KestrelViewerMessageCallbackAPI
) => {
  if (messageJson.type === undefined) return

  for (const handler of messageHandlers) {
    if (handler.type !== messageJson.type) continue
    if (
      handler.requiredData.filter((r: string) => messageJson[r] === undefined)
        .length > 0
    )
      continue
    handler.callback(messageJson as Record<string, unknown>, kestrelViewerAPI)
  }
}

const parseMessage = (dataMessage: string): ParsedMessage | null => {
  try {
    return JSON.parse(dataMessage) as ParsedMessage
  } catch {
    // Disregard, built-in messages aren't JSON
    return null
  }
}
/********* End Helpers for message handling *********/

export const PixelStreamUI: FC = () => {
  const { user = { name: '', email: '' }, authorization } = useGeckoAuth()
  const { rolodexPatch } = useRolodexEdit()
  const { KESTREL_API_URL } = useRuntimeVariables()
  const {
    authorizedAxios,
    pixelStreamingURL,
    setPixelStreamingURL,
    kestrelStatus,
    setKestrelStatus,
    selectedMarkup,
    setSelectedMarkup,
    setSendKVCommand,
    popupMenuStartPosition,
    setPopupMenuStartPosition,
    kestrelModel,
    annotations,
    setAnnotations,
    polygons,
    setPolygons,
    measurements,
    setMeasurements,
    surfaceAreas,
    setSurfaceAreas,
    planeIntersections,
    setPlaneIntersections,
    cameraViews,
    setCameraViews,
  } = useKestrel()

  // Load photos for the kestrel model
  const {
    photos: serverPhotos,
    isLoading: photosLoading,
    error: photosError,
  } = usePhotoFiles(
    kestrelModel?.data.photosEnabled ? kestrelModel.id : undefined
  )

  const { cameraPoses: cameraPosesFromRolodex } = useCameraPoses(
    kestrelModel?.id
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const videoParent = useRef<HTMLDivElement>(null)
  const [pixelStreaming, setPixelStreaming] = useState<PixelStreaming>()
  const [viewerId, setViewerId] = useState('-1')
  const [selectedMode, setSelectedMode] = useState('cursor')
  const [measurementType, setMeasurementType] = useState('straightLine')
  const mouseTrackerRef = useRef<Vector2>(new Vector2(0, 0))
  const [helperTextVisible, setHelperTextVisible] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [settings, setSettings] = useState<KVSettings>()
  const [is2DPlaneModeActive, setIs2DPlaneModeActive] = useState(false)
  const [planePosition, setPlanePosition] = useState<KVPosition>()
  const [planeRotation, setPlaneRotation] = useState<KVRotation>()
  const [photoReelOpen, setPhotoReelOpen] = useState(false)
  const [g1n5FormOpen, setG1n5FormOpen] = useState(false)
  const [g1n5, setG1n5] = useState<G1N5 | undefined>(undefined)
  const [photos, setPhotos] = useState<KVPhoto[]>([])
  const [selectedPhoto, setSelectedPhoto] = useState<KVPhoto | null>(null)
  const [filteredPhotos, setFilteredPhotos] = useState<KVPhoto[] | null>(null)
  const [navigationMode, setNavigationMode] =
    useState<KVNavigationMode>('orbit')
  const [cameraPoses, setCameraPoses] = useState<KVCameraPose[] | null>([])
  const [filteringPhotos, setFilteringPhotos] = useState(false)

  // Sync local photos state with server photos
  useEffect(() => {
    setPhotos(serverPhotos)
  }, [serverPhotos])

  // Sync local camera poses state with camera poses from rolodex
  useEffect(() => {
    setCameraPoses(cameraPosesFromRolodex)
  }, [cameraPosesFromRolodex])

  const messageHandlers = useMemo(() => {
    return getAllMessages()
  }, [])

  const kestrelViewerAPI = useMemo(() => {
    return {
      setViewerId,
      pixelStream: pixelStreaming,
      user: { name: user.name, email: user.email },
      videoParent,
      setSelectedMarkup,
      mouseTrackerRef: mouseTrackerRef,
      setPopupMenuStartPosition,
      annotations,
      setAnnotations,
      settings,
      setSettings,
      polygons,
      setPolygons,
      measurements,
      setMeasurements,
      surfaceAreas,
      setSurfaceAreas,
      cameraViews,
      setCameraViews,
      navigationMode,
      setNavigationMode,
      rolodexPatch,
      kestrelModel,
      planePosition,
      setPlanePosition,
      planeRotation,
      setPlaneRotation,
      planeIntersections,
      setPlaneIntersections,
      photoReelOpen,
      setPhotoReelOpen,
      selectedPhoto,
      setSelectedPhoto,
      photos,
      setPhotos,
      filteredPhotos,
      setFilteredPhotos,
      authorizedAxios,
      kestrelApiUrl: KESTREL_API_URL,
      cameraPoses,
      setCameraPoses,
      filteringPhotos,
      setFilteringPhotos,
    } as KestrelViewerMessageCallbackAPI
  }, [
    setViewerId,
    pixelStreaming,
    videoParent,
    setSelectedMarkup,
    mouseTrackerRef,
    setPopupMenuStartPosition,
    annotations,
    settings,
    setSettings,
    setAnnotations,
    polygons,
    setPolygons,
    measurements,
    setMeasurements,
    surfaceAreas,
    setSurfaceAreas,
    cameraViews,
    setCameraViews,
    navigationMode,
    user,
    setNavigationMode,
    rolodexPatch,
    kestrelModel,
    planePosition,
    setPlanePosition,
    planeRotation,
    setPlaneRotation,
    planeIntersections,
    setPlaneIntersections,
    photoReelOpen,
    setPhotoReelOpen,
    selectedPhoto,
    setSelectedPhoto,
    photos,
    setPhotos,
    filteredPhotos,
    setFilteredPhotos,
    authorizedAxios,
    KESTREL_API_URL,
    cameraPoses,
    setCameraPoses,
    filteringPhotos,
    setFilteringPhotos,
  ])

  useEffect(() => {
    if (!videoParent.current) return

    const defaultSettings = {
      AutoPlayVideo: true,
      AutoConnect: true,
      ss: pixelStreamingURL,
      StartVideoMuted: true,
      HoveringMouse: true,
      WaitForStreamer: true,
    }

    const mouseListener = (e: MouseEvent) => {
      if (mouseTrackerRef.current) {
        mouseTrackerRef.current.x = e.clientX
        mouseTrackerRef.current.y = e.clientY
      }
    }
    document.body.addEventListener('mousemove', mouseListener)

    const config = new Config({ initialSettings: defaultSettings })
    const pixelStream = new PixelStreaming(config, {
      videoElementParent: videoParent.current,
    })

    // Wait for websocket to be ready before sending auth, re-sending auth after a disconnect/reconnect cycle
    //
    // We are monkey patching `pixelStream.webSocketController.handleOnOpen`
    // because it's the only callback we could find that triggered with the
    // websocket connection in an open state after the initial connection _and_
    // after reconnects.
    //
    // When we assign `handleOnOpen` to the local `prevOnOpen`, `prevOnOpen` is
    // not a method on the `pixelStream.webSocketController` object. We call
    // `bind` to make it a method so it works correctly.
    const prevOnOpen = pixelStream.webSocketController.handleOnOpen.bind(
      pixelStream.webSocketController
    )
    const handleOnOpen = (e: Event) => {
      prevOnOpen(e)
      pixelStream.webSocketController.webSocket.send(
        JSON.stringify({
          type: 'geckoToken',
          value: authorization?.split(' ')[1],
        })
      )
    }
    pixelStream.webSocketController.handleOnOpen = handleOnOpen

    setPixelStreaming(pixelStream)
    setAnnotations(kestrelModel?.data.annotations ?? [])
    setPolygons(kestrelModel?.data.polygons ?? [])
    setMeasurements(kestrelModel?.data.measurements ?? [])
    setPlaneIntersections(kestrelModel?.data.planeIntersections ?? [])
    setSurfaceAreas(kestrelModel?.data.surfaceAreas ?? [])
    setCameraViews(kestrelModel?.data.cameraViews ?? [])
    setG1n5(kestrelModel?.data.g1n5 ?? undefined)
    setSendKVCommand(() => {
      return (json: string | object) => {
        if (!pixelStream) return
        pixelStream.emitUIInteraction(json)
      }
    })

    // @ts-expect-error Planning to fork / reimplement pixel stream entirely soon
    pixelStream._webRtcController.prevOnMessage =
      // @ts-expect-error Planning to fork / reimplement pixel stream entirely soon
      pixelStream._webRtcController.handleOnMessage

    return () => {
      try {
        pixelStream.disconnect()
      } catch {
        // TODO: need to log this?
      }
      document.body.removeEventListener('mousemove', mouseListener)
    }
  }, [
    messageHandlers,
    setSelectedMarkup,
    setSendKVCommand,
    mouseTrackerRef,
    setAnnotations,
    setCameraViews,
    setPolygons,
    setMeasurements,
    setPlaneIntersections,
    setSurfaceAreas,
    pixelStreamingURL,
    authorization,
    kestrelModel,
  ])

  // Separate effect for window event listeners
  useEffect(() => {
    if (!pixelStreaming || !kestrelStatus?.videoReady) return

    const handleWindowBlur = () => {
      // Send 'playerAway' command to Unreal Engine
      pixelStreaming.emitUIInteraction({
        command: 'playerAway',
      })
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Send 'playerAway' command to Unreal Engine
        pixelStreaming.emitUIInteraction({
          command: 'playerAway',
        })
      }
    }

    window.addEventListener('blur', handleWindowBlur)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('blur', handleWindowBlur)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [pixelStreaming, kestrelStatus?.videoReady])

  useEffect(() => {
    if (!pixelStreaming) return
    const messageChunks = new Map<string, ChunkBuffer>()
    // @ts-expect-error Planning to fork / reimplement pixel stream entirely soon
    pixelStreaming._webRtcController.handleOnMessage = (ev) => {
      const dec = new TextDecoder('utf-8')
      const dataMessage = dec.decode(ev.data).replaceAll('\x00', '')
      const dataJson = parseMessage(dataMessage)

      if (dataJson) {
        // Check if this is a chunked message
        if (dataJson.__chunkType === 'partial' && dataJson.__messageId) {
          const completeMessage = processChunkedMessage(
            dataJson.__messageId,
            messageChunks,
            {
              __totalChunks: dataJson.__totalChunks as number,
              __chunkIndex: dataJson.__chunkIndex as number,
              __chunkData: dataJson.__chunkData as string,
            }
          )

          if (completeMessage) {
            try {
              const completeJson = JSON.parse(completeMessage) as ParsedMessage
              handleMessageWithHandlers(
                completeJson,
                messageHandlers as MessageHandler[],
                kestrelViewerAPI
              )
            } catch (error) {
              console.error('Error parsing reassembled message:', error)
            }
          }
        } else {
          handleMessageWithHandlers(
            dataJson,
            messageHandlers as MessageHandler[],
            kestrelViewerAPI
          )
        }
      }

      // @ts-expect-error Planning to fork / reimplement pixel stream entirely soon
      pixelStreaming._webRtcController.prevOnMessage(ev)
    }
  }, [pixelStreaming, kestrelViewerAPI, messageHandlers])

  const sendHeartbeat = useMemo(() => {
    return () => {
      if (!pixelStreaming) return
      pixelStreaming.emitUIInteraction({
        heartbeat: user.name,
        heartbeatId: viewerId,
      })
    }
  }, [pixelStreaming, viewerId, user.name])

  const sendCommand = useMemo(() => {
    return (json: string | object) => {
      if (!pixelStreaming) return
      pixelStreaming.emitUIInteraction(json)
    }
  }, [pixelStreaming])

  const onVideoInitialized = useCallback(() => {
    if (!pixelStreaming) return

    setKestrelStatus({
      ...kestrelStatus,
      videoReady: true,
    })

    // Send polygons and annotations to Unreal Engine
    if (kestrelModel?.data) {
      pixelStreaming.emitUIInteraction({
        command: 'loadPolygons',
        polygons: kestrelModel.data.polygons.map((polygon) =>
          KVPolygon.toJSON(polygon)
        ),
      })
      pixelStreaming.emitUIInteraction({
        command: 'loadAnnotations',
        annotations: kestrelModel.data.annotations.map((annotation) =>
          KVAnnotation.toJSON(annotation)
        ),
      })
      pixelStreaming.emitUIInteraction({
        command: 'loadMeasurements',
        measurements: kestrelModel.data.measurements.map((measurement) =>
          KVMeasurement.toJSON(measurement)
        ),
      })
      pixelStreaming.emitUIInteraction({
        command: 'loadSurfaceAreas',
        surfaceAreas: kestrelModel.data.surfaceAreas.map((surfaceArea) =>
          KVSurfaceArea.toJSON(surfaceArea)
        ),
      })
      // Sets the measurement scale after the measurements are loaded
      pixelStreaming.emitUIInteraction({
        command: 'setMeasurementScale',
        measurementScale: kestrelModel.data.measurementScale,
      })
    }

    const heartbeatFunc = () => {
      // TODO: this function sticks around even after the stream has disconnected
      sendHeartbeat()
      setTimeout(() => heartbeatFunc(), 1000)
    }
    heartbeatFunc()

    startWebRTCStatsCollection(pixelStreaming)
  }, [
    pixelStreaming,
    sendHeartbeat,
    kestrelStatus,
    setKestrelStatus,
    kestrelModel,
  ])

  useEffect(() => {
    if (!pixelStreaming || !videoParent.current) return
    pixelStreaming.addEventListener('videoInitialized', onVideoInitialized)
    return () => {
      pixelStreaming.removeEventListener('videoInitialized', onVideoInitialized)
    }
  }, [pixelStreaming, onVideoInitialized])

  const updateAnnotation = useMemo(
    () =>
      (
        annotation: KVAnnotation,
        jsonFields: string | string[],
        setter: (a: KVAnnotation) => KVAnnotation,
        v: unknown | unknown[]
      ) => {
        updateMarkup({
          markup: annotation,
          jsonFields,
          setter,
          v,
          commandType: 'updateAnnotation',
          markupArray: annotations,
          setMarkupArray: setAnnotations,
          rolodexField: 'annotations',
          toJSON: KVAnnotation.toJSON,
          sendCommand,
          selectedMarkup,
          setSelectedMarkup,
          kestrelModelId: kestrelModel?.id ?? '',
          rolodexPatch,
        })
      },
    [
      selectedMarkup,
      setSelectedMarkup,
      sendCommand,
      annotations,
      setAnnotations,
      kestrelModel,
      rolodexPatch,
    ]
  )

  const updatePolygon = useMemo(
    () =>
      (
        polygon: KVPolygon,
        jsonFields: string | string[],
        setter: (a: KVPolygon) => KVPolygon,
        v: unknown | unknown[]
      ) => {
        updateMarkup({
          markup: polygon,
          jsonFields,
          setter,
          v,
          commandType: 'updatePolygon',
          markupArray: polygons,
          setMarkupArray: setPolygons,
          rolodexField: 'polygons',
          toJSON: KVPolygon.toJSON,
          sendCommand,
          selectedMarkup,
          setSelectedMarkup,
          kestrelModelId: kestrelModel?.id ?? '',
          rolodexPatch,
        })
      },
    [
      selectedMarkup,
      setSelectedMarkup,
      sendCommand,
      polygons,
      setPolygons,
      kestrelModel,
      rolodexPatch,
    ]
  )

  const updateMeasurement = useMemo(
    () =>
      (
        measurement: KVMeasurement,
        jsonFields: string | string[],
        setter: (a: KVMeasurement) => KVMeasurement,
        v: string | boolean | (string | boolean)[]
      ) => {
        updateMarkup({
          markup: measurement,
          jsonFields,
          setter,
          v,
          commandType: 'updateMeasurement',
          markupArray: measurements,
          setMarkupArray: setMeasurements,
          rolodexField: 'measurements',
          toJSON: KVMeasurement.toJSON,
          sendCommand,
          selectedMarkup,
          setSelectedMarkup,
          kestrelModelId: kestrelModel?.id ?? '',
          rolodexPatch,
        })
      },
    [
      selectedMarkup,
      setSelectedMarkup,
      sendCommand,
      measurements,
      setMeasurements,
      kestrelModel,
      rolodexPatch,
    ]
  )

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
      }}>
      {/* Necessary to nest the video parent as its styles are overwritten */}
      <div ref={videoParent}></div>
      <KestrelViewerProvider
        value={{
          sendCommand,
          selectedMode,
          setSelectedMode,
          selectedMarkup,
          setSelectedMarkup,
          popupMenuStartPosition,
          updateAnnotation,
          updatePolygon,
          updateMeasurement,
          helperTextVisible,
          setHelperTextVisible,
          settingsOpen,
          setSettingsOpen,
          settings,
          setSettings,
          is2DPlaneModeActive,
          setIs2DPlaneModeActive,
          planePosition,
          setPlanePosition,
          planeRotation,
          setPlaneRotation,
          measurementType,
          setMeasurementType,
          planeIntersections,
          setPlaneIntersections,
          cameraViews,
          setCameraViews,
          photoReelOpen,
          setPhotoReelOpen,
          g1n5FormOpen,
          setG1n5FormOpen,
          g1n5,
          setG1n5,
          selectedPhoto,
          setSelectedPhoto,
          photos,
          setPhotos,
          filteredPhotos,
          setFilteredPhotos,
          photosLoading,
          photosError,
          containerRef,
          kestrelModel,
          pixelStreamingURL,
          setPixelStreamingURL,
          setSendKVCommand,
          setPopupMenuStartPosition,
          surfaceAreas,
          setSurfaceAreas,
          navigationMode,
          setNavigationMode,
          cameraPoses,
          setCameraPoses,
          filteringPhotos,
          setFilteringPhotos,
        }}>
        <KestrelViewerUI />
      </KestrelViewerProvider>
    </div>
  )
}
